import React from "react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { classNames } from "Utils/utils";
import PaEmptyState from "./PaEmptyState/PaEmptyState";

const SortIndicator = ({ active, direction }) => {
  if (!active) {
    return null;
  }

  return direction === "asc" ? (
    <HiChevronUp className="ml-1 inline h-4 w-4 text-indigo-600" aria-hidden="true" />
  ) : (
    <HiChevronDown className="ml-1 inline h-4 w-4 text-indigo-600" aria-hidden="true" />
  );
};

const PaDataTable = ({
  columns = [],
  rows = [],
  rowKey = "id",
  sortKey = null,
  sortDirection = "asc",
  onSort,
  onRowClick = null,
  emptyState = null,
  isLoading = false,
  loadingRows = 5,
  className,
}) => {
  const resolveRowKey = (row, index) => {
    if (typeof rowKey === "function") {
      return rowKey(row, index);
    }
    return row?.[rowKey] ?? index;
  };

  const renderEmpty = () => {
    if (emptyState) {
      return emptyState;
    }

    return <PaEmptyState title="No results" message="Nothing to show yet" />;
  };

  return (
    <div
      className={classNames(
        "overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm",
        className
      )}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr>
              {columns.map((column) => {
                const isSortable = column.sortable && typeof onSort === "function";
                const isActive = sortKey === column.key;

                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={classNames(
                      "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500",
                      column.headerClassName
                    )}
                  >
                    {isSortable ? (
                      <button
                        type="button"
                        onClick={() => onSort(column.key)}
                        className="inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      >
                        {column.label ?? column.header}
                        <SortIndicator
                          active={isActive}
                          direction={isActive ? sortDirection : "asc"}
                        />
                      </button>
                    ) : (
                      column.label ?? column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {isLoading
              ? Array.from({ length: loadingRows }).map((_, index) => (
                  <tr key={`loading-${index}`} className="animate-pulse">
                    {columns.map((column) => (
                      <td key={column.key} className="px-4 py-4">
                        <div className="h-4 rounded bg-gray-100" />
                      </td>
                    ))}
                  </tr>
                ))
              : null}
            {!isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length || 1} className="px-4 py-8">
                  {renderEmpty()}
                </td>
              </tr>
            ) : null}
            {!isLoading
              ? rows.map((row, index) => {
                  const isClickable = typeof onRowClick === "function";

                  return (
                  <tr
                    key={resolveRowKey(row, index)}
                    title={isClickable ? "View tenant details" : undefined}
                    onClick={isClickable ? () => onRowClick(row, index) : undefined}
                    onKeyDown={
                      isClickable
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onRowClick(row, index);
                            }
                          }
                        : undefined
                    }
                    tabIndex={isClickable ? 0 : undefined}
                    className={classNames(
                      "transition-colors hover:bg-indigo-50/40",
                      isClickable &&
                        "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500"
                    )}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={classNames(
                          "whitespace-nowrap px-4 py-3 text-sm text-gray-700",
                          column.cellClassName
                        )}
                      >
                        {typeof column.render === "function"
                          ? column.render(row, index)
                          : row?.[column.key]}
                      </td>
                    ))}
                  </tr>
                  );
                })
              : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaDataTable;
